<script lang="ts" context="module">
  import type {
    PriceAlert,
    PriceAlertInput,
    Race,
    RacesResponse
  } from '$types/races';

  export const priceAlertsDialog = createDialog({ label: 'Price Alerts' });
  const priceAlertInput = writable<PriceAlertInput | null>(null);

  export function populatePriceAlert(alert: PriceAlertInput) {
    priceAlertInput.set(alert);
    priceAlertsDialog.open();
  }
  export function clearPriceAlert() {
    priceAlertInput.set(null);
  }
</script>

<script lang="ts">
  import { page } from '$app/stores';
  import { removePriceAlert } from '$utils/alerts';
  import { createDialog } from 'svelte-headlessui';
  import Transition from 'svelte-transition';
  import { writable } from 'svelte/store';
  import AddPriceAlertContainer from './AddPriceAlertContainer.svelte';
  import PriceAlertsListHeader from './PriceAlertsListHeader.svelte';
  import RaceAlertsListItem from './RaceAlertsListItem.svelte';

  $: ({ priceAlerts, races } = $page.data);

  let alertsByRace: Map<string, RaceAlertsGrouping>;
  interface RaceAlertsGrouping {
    race: Race;
    alerts: PriceAlert[];
  }

  $: {
    // group the alerts into a map with the key being the raceId and the value being a list of the alerts
    alertsByRace = new Map<string, RaceAlertsGrouping>(
      [
        ...($priceAlerts?.data?.priceAlerts ?? []).reduce((acc, alert) => {
          if (alert) {
            if (!acc.has(alert.eventId)) {
              const race = $races?.data?.races.find(
                (r) => r.id === alert.eventId
              );
              if (!race) return acc;
              acc.set(alert.eventId, { race: race, alerts: [alert] });
            } else {
              const existingAlerts = acc.get(alert.eventId);
              if (!existingAlerts) return acc;
              existingAlerts.alerts.push(alert);
              acc.set(alert.eventId, existingAlerts);
            }
          }
          return acc;
        }, new Map<string, RaceAlertsGrouping>())
      ].sort((a, b) => {
        if (!a[1].race.startTime?.valueOf()) return 1;
        if (!b[1].race.startTime?.valueOf()) return -1;
        return a[1].race.startTime?.valueOf() - b[1].race.startTime?.valueOf();
      })
    );
  }
</script>

<div class="absolute z-50">
  <Transition show={$priceAlertsDialog.expanded}>
    <Transition
      enter="ease-out duration-300"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="ease-in duration-200"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class="fixed inset-0 bg-black bg-opacity-25"
        on:click={priceAlertsDialog.close}
        on:keypress={priceAlertsDialog.close}
      />
    </Transition>

    <div
      class="fixed inset-0 flex h-full w-full items-center justify-center overflow-auto"
    >
      <div
        class="flex h-3/4 w-5/6 items-center justify-center overflow-y-auto p-4 text-center md:w-3/4 lg:w-1/2"
      >
        <Transition
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <div
            class="relative flex h-full w-full max-w-screen-lg transform flex-col gap-y-3 overflow-y-auto rounded-2xl bg-white p-4 text-left align-middle shadow-xl transition-all"
            use:priceAlertsDialog.modal
          >
            <div class="flex flex-row items-center gap-x-2">
              <div class="flex-1">
                <h2
                  class="text-sm font-semibold leading-7 text-gray-900 md:text-base"
                >
                  Add Price Alert
                </h2>
                <p
                  class="mt-1 max-w-2xl text-xs leading-6 text-gray-600 md:text-sm"
                >
                  Price alerts will be sent as a browser notification as well as
                  to your Telegram account if configured.
                </p>
              </div>
            </div>
            {#if $priceAlertInput?.runner}
              <AddPriceAlertContainer priceAlertInput={$priceAlertInput} />
            {/if}
            {#if !alertsByRace?.size}
              <div class="rounded-lg border border-gray-200 bg-gray-50 p-5">
                <p class="mt-1 text-sm font-semibold text-gray-700">
                  No active alerts.
                </p>
                <p class="mt-1 text-sm text-gray-500">
                  Add an alert by hovering over a price and clicking "Add Price
                  Alert"
                </p>
              </div>
            {/if}
            <ul class="divide-y overflow-y-auto rounded-md">
              {#each [...alertsByRace] as [raceId, raceAlertsGrouping] (raceId)}
                <PriceAlertsListHeader race={raceAlertsGrouping.race} />
                {#each raceAlertsGrouping.alerts ?? [] as alert (alert)}
                  {#if alert}
                    <RaceAlertsListItem
                      {alert}
                      race={raceAlertsGrouping.race}
                      on:delete={(e) => removePriceAlert(e.detail)}
                    />
                  {/if}
                {/each}
              {/each}
            </ul>
          </div>
        </Transition>
      </div>
    </div>
  </Transition>
</div>
